import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Dialog,
  DialogContent,
  Tab,
  Tabs,
  Paper,
  Typography,
} from '@material-ui/core';

import { makeStyles } from '@material-ui/core/styles';

import StatusIcon from '../common/StatusIcon';
import TabPanel from '../common/TabPanel';

import {
  selectGuest,
  selectReservation,
  selectBackgroundMatch,
  getGuestById,
  getBackgroundCheckByGuestId,
  createBackgroundCheckSession,
} from '../../store/widgetSlice';

import {
  setMessage,
} from '../../store/authSlice';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    maxWidth: '100%',
  },
  table: {
    marginLeft: theme.spacing(4),
    marginRight: theme.spacing(4),
    width: `calc(100% - ${theme.spacing(8)}px)`,
  },
  row: {
    justifyContent: 'space-between',
  },
  cell: {
    padding: 0,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),

    'tr:first-child &': {
      borderTop: '1px solid rgba(224, 224, 224, 1)',
    },

    'tr:last-child &': {
      borderBottom: 0,
    },
  },
  panel: {
    backgroundColor: theme.palette.background.default,
    minWidth: 550,
  },
  dialog: {
    padding: 0,
  },
  heading: {
    fontSize: 14,
    fontWeight: 600,
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    alignSelf: 'top',
  },
  cardHeading: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  name: {
    fontSize: 18,
    fontWeight: 600,
    textAlign: 'center',
  },
  center: {
    textAlign: 'center',
  },
  noMArgin: {
    margin: 0,
  },
  equalHeight: {
    height: '100%',
  },
  ratingBox: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'space-between',
  },
  numberOfReviews: {
    fontSize: 14,
    fontWeight: 600,
    padding: 0,
  },
  ratingBars: {
    marginLeft: theme.spacing(4),
    marginRight: theme.spacing(4),
    paddingBottom: theme.spacing(1),
  },
  tableIcon: {
    verticalAlign: 'middle',
  },
  card: {
    display: 'flex',
    marginTop: 10,
    marginBottom: 10,
  },
  cardMedia: {
    display: 'flex',
    maxWidth: 150,
  },
  cardImage: {
    width: '100%',
    objectFit: 'cover',
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    margin: 20,
    flexGrow: 1,
  },
}));

export default function BackgroundModal({
  hideDefaultButton,
  externalOpen,
  externalHandleClose,
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const guest = useSelector(selectGuest);
  const reservation = useSelector(selectReservation);
  const session = useSelector(selectBackgroundMatch);

  const [open, setOpen] = React.useState(false);
  const [currentTab, setCurrentTab] = React.useState(0);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (open && guest && guest.id) {
      dispatch(getBackgroundCheckByGuestId({ guestId: guest.id }))
        .unwrap()
        .then(() => {
          dispatch(setMessage({ message: 'Match information retrieved', severity: 'success' }));
        })
        .catch(() => {
          if (reservation) {
            dispatch(setMessage({ message: 'Retrieving match information. Please wait...', severity: 'info' }));
            dispatch(createBackgroundCheckSession({
              companyCode: reservation.companyCode,
              reservationCode: reservation.reservationCode,
            }))
              .unwrap()
              .then(() => {
                dispatch(getGuestById({ guestId: guest.id }));
                dispatch(setMessage({ message: 'Match information found', severity: 'success' }));
              })
              .catch(() => {
                dispatch(setMessage({ message: 'Match information not found', severity: 'error' }));
              });
          }
        });
    }
  }, [dispatch, guest, reservation, open]);

  useEffect(() => {
    setOpen(externalOpen);
  }, [externalOpen]);

  return (
    <div>
      {
        !hideDefaultButton
        && (
          <button className="grwidget__status-button" type="button" onClick={handleClickOpen}>
            <div className="grwidget__status-item">
              <span className="grwidget__status-label">Background</span>
              <span className="grwidget__status-icon">
                <StatusIcon status={guest && guest.backgroundVerified} />
              </span>
            </div>
          </button>
        )
      }
      <Dialog
        className={classes.dialog}
        open={open}
        onClose={externalHandleClose || handleClose}
        maxWidth="sm"
      >
        <Paper>
          <Tabs
            value={currentTab}
            indicatorColor="primary"
            textColor="primary"
            onChange={handleTabChange}
            variant="fullWidth"
          >
            <Tab label="Background Check" />
          </Tabs>
        </Paper>
        <DialogContent className={classes.dialog}>
          <TabPanel className={classes.panel} value={currentTab} index={0}>
            {session && session.summary && session.summary.status
              && (
              <div className={classes.name}>
                <StatusIcon
                  className={classes.tableIcon}
                  status={session && session.summary && session.summary.status}
                  style={{ fontSize: 80 }}
                />
                <p>
                  <strong>Background Check: </strong>
                  {session && session.summary && session.summary.message}
                </p>
              </div>
              )}
            { (session && session.results && session.results.length > 0)
              ? (
                <>
                  <div className={classes.center}>
                    <p>
                      {session.results.length}
                      {' '}
                      { session.results.length === 1 ? 'result ' : 'results '}
                      found on our national criminal databases.
                    </p>
                  </div>
                  {session.results.map((result) => (
                    <Paper className={classes.card} component={Paper} elevation="1">
                      <div className={classes.cardMedia}>
                        <img
                          className={classes.cardImage}
                          src={result.photo}
                          alt={session.reference}
                        />
                      </div>
                      <div className={classes.cardContent}>
                        <Typography
                          className={classes.noMargin}
                          component="h2"
                          variant="h6"
                        >
                          {result.fullname}
                        </Typography>
                        <Typography
                          className={classes.noMargin}
                          component="p"
                          variant="body"
                        >
                          Category:
                          {' '}
                          {result.category}
                        </Typography>
                        <Typography
                          className={classes.noMargin}
                          component="p"
                          variant="body"
                        >
                          Age:
                          {' '}
                          {result.age || 'N/A'}
                        </Typography>
                        <Typography
                          className={classes.noMargin}
                          component="p"
                          variant="body"
                        >
                          Sex:
                          {' '}
                          {result.sex || 'N/A'}
                        </Typography>
                        <Typography
                          className={classes.noMargin}
                          component="p"
                          variant="body"
                        >
                          City:
                          {' '}
                          {result.city || 'N/A'}
                        </Typography>
                        <Typography
                          className={classes.noMargin}
                          component="p"
                          variant="body"
                        >
                          State:
                          {' '}
                          {result.state || 'N/A'}
                        </Typography>
                        <Typography
                          className={classes.noMargin}
                          component="p"
                          variant="body"
                        >
                          Zip Code:
                          {' '}
                          {result.zip || 'N/A'}
                        </Typography>
                        <Typography
                          className={classes.noMargin}
                          component="p"
                          variant="body"
                        >
                          Source:
                          {' '}
                          {result.sourceName}
                        </Typography>
                      </div>
                    </Paper>
                  ))}
                </>
              )
              : (
                <div className={classes.center}>
                  <p>No results found on our national criminal databases.</p>
                </div>
              )}
          </TabPanel>
        </DialogContent>
      </Dialog>
    </div>
  );
}
